// Wspólna warstwa dostępu do DRF dla kontrolerów Stimulus.
// Session auth (cookie) + CSRF z <meta name="csrf-token"> (fallback: cookie csrftoken).

export function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  if (meta?.content) return meta.content
  return (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || ""
}

// GET JSON — rzuca Error przy statusie != 2xx (kontroler pokazuje stan błędu).
export async function getJSON(url) {
  const res = await fetch(url, { headers: { Accept: "application/json" } })
  if (!res.ok) {
    const error = new Error(`GET ${url} -> ${res.status}`)
    error.status = res.status
    throw error
  }
  return res.json()
}

// Mutacja JSON — NIE rzuca: zwraca { ok, status, data }, kontroler decyduje po statusie.
// Brak fallbacku "udawanego sukcesu": nieudany zapis nie może wyglądać jak udany.
export async function sendJSON(method, url, body) {
  // a tutorial is running on this page: nothing may write (dsroll-b#227) — the shape of a failed request, with the reason
  if (document.body?.hasAttribute("data-tour-running") && String(method).toUpperCase() !== "GET") {
    return { ok: false, status: 0, data: { detail: "Niedostępne w trakcie samouczka" } }
  }
  let res
  try {
    res = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken(),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    return { ok: false, status: 0, data: null }
  }
  let data = null
  try { data = await res.json() } catch { /* 204 lub nie-JSON */ }
  return { ok: res.ok, status: res.status, data }
}

// The first nonempty human message in a DRF exception payload (string, list or nested field map).
function firstErrorMessage(value) {
  if (typeof value === "string") return value.trim() ? value : null
  const children = Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : []
  for (const child of children) {
    const message = firstErrorMessage(child)
    if (message !== null) return message
  }
  return null
}

// Unwrap the project exception envelope or a plain DRF detail response.
export function errorDetail(data, fallback) {
  const detail = data?.error?.detail ?? data?.detail
  return firstErrorMessage(detail) ?? fallback
}

const GENERIC_ERROR = "Wystąpił błąd. Spróbuj ponownie."

// The form's own label for a field name ("county" → "Powiat"); the key itself when the form has
// no such field. The label's first text node skips the "*" / "(opcjonalnie)" spans.
function fieldLabel(key, root) {
  const input = root?.querySelector?.(`[name="${key}"]`)
  // A text input has label[for]; a radio group is named by its field's .ls-label.
  const label =
    (input?.id ? root.querySelector(`label[for="${input.id}"]`) : null) ??
    input?.closest(".ls-field")?.querySelector(".ls-label")
  const text = label?.firstChild?.textContent?.trim()
  return text || key
}

// One readable message from a failed form POST. Understands the project envelope
// {"error": {"detail": ...}} and the flat DRF shape, and walks nested errors (placements).
export function formErrorMessage(data, root) {
  if (!data) return GENERIC_ERROR
  // Field maps must retain all messages and their labels below, rather than only the first one.
  const text = data?.error?.detail ?? data?.detail
  if (typeof text === "string" && text) return text
  const detail = data?.error?.detail ?? data
  if (!detail || typeof detail !== "object") return GENERIC_ERROR
  const parts = []
  const walk = (key, value) => {
    if (typeof value === "string") {
      parts.push(key === "non_field_errors" ? value : `${fieldLabel(key, root)}: ${value}`)
    } else if (Array.isArray(value)) {
      value.forEach((item) => walk(key, item))
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([k, v]) => walk(k, v))
    }
  }
  for (const [key, value] of Object.entries(detail)) {
    if (key !== "status_code") walk(key, value)
  }
  return parts.length ? parts.join("; ") : GENERIC_ERROR
}

// The element that shows a field's error border: the input itself, a radio group's row, or a
// combobox's visible box (its value lives in a hidden input).
function errorBox(input) {
  if (input.type === "radio") return input.closest(".ls-radio-row") ?? input
  if (input.type === "hidden") return input.closest('[data-controller~="combobox"]')?.querySelector(".ls-combo__box") ?? null
  return input
}

// Red-border every field named in a failed POST, scroll to and focus the first one; each mark
// clears once its field is edited. Returns the first marked element, or null when the error
// names no field of this form (the caller then keeps its general notice in view).
export function markFieldErrors(data, root) {
  root?.querySelectorAll?.(".ls-invalid").forEach((el) => {
    el.classList.remove("ls-invalid")
    el.removeAttribute("aria-invalid")
  })
  const detail = data?.error?.detail ?? data
  if (!root || !detail || typeof detail !== "object" || Array.isArray(detail)) return null
  let first = null
  for (const key of Object.keys(detail)) {
    if (key === "status_code" || key === "non_field_errors") continue
    for (const input of root.querySelectorAll(`[name="${key}"]`)) {
      const box = errorBox(input)
      if (!box) continue
      box.classList.add("ls-invalid")
      box.setAttribute("aria-invalid", "true")
      const clear = () => {
        box.classList.remove("ls-invalid")
        box.removeAttribute("aria-invalid")
      }
      input.addEventListener("input", clear, { once: true })
      input.addEventListener("change", clear, { once: true })
      first ??= box
    }
  }
  if (first) {
    first.scrollIntoView?.({ behavior: "smooth", block: "center" })
    const focusable = first.matches("input, select, textarea") ? first : first.querySelector("input, select, textarea")
    focusable?.focus({ preventScroll: true })
  }
  return first
}

// "320.00" | 320 -> "320 zł"; "73.60" -> "73,60 zł"
export function formatPln(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return "—"
  const rounded = Math.round(num * 100) / 100
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(".", ",")
  return `${text} zł`
}

/**
 * Formatuje etykietę podkategorii: „Kategoria › Podkategoria".
 * Jedna implementacja współdzielona przez backorder-controller (lista) i subcat-search-controller (autocomplete).
 * @param {{category_name?: string|null, name: string}} item
 * @returns {string}
 */
export function formatSubcatLabel(item) {
  return item.category_name ? `${item.category_name} › ${item.name}` : item.name
}

// ISO -> "01.07.2026, 14:32" (pl-PL)
export function formatDate(iso) {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })
}

// Wyniki z listowego endpointu DRF (paginowane {results} albo goła lista).
export function listResults(data) {
  return Array.isArray(data) ? data : (data?.results ?? [])
}

const MAX_JSON_PAGES = 200

// Follow every next page for complete dictionaries and joins. The hard safety ceiling always
// throws instead of returning an incomplete list; callers may request a smaller ceiling.
export async function getAllJSON(url, maxPages = MAX_JSON_PAGES) {
  const out = []
  let next = url
  const pageLimit = Math.min(maxPages, MAX_JSON_PAGES)
  for (let i = 0; i < pageLimit && next; i += 1) {
    const data = await getJSON(next)
    if (Array.isArray(data)) return [...out, ...data]
    out.push(...(data.results ?? []))
    next = data.next
  }
  if (next) throw new Error(`getAllJSON: ${pageLimit}-page pagination limit exceeded; more rows remain`)
  return out
}
